/**
 * ErrorBoundary - Catches FATAL React errors only
 * 
 * Error Classification:
 * - fatal_ui_error: render crash, invariant violation, undefined access → shows fallback
 * - data_error, permission_error, network_error → should be handled by components, not boundary
 * 
 * Preserves local storage and allows returning to previous working screen
 */

import { Component, type ReactNode, useState } from 'react';
import { ChevronLeft, RefreshCw, AlertTriangle, MessageSquare, Copy, Check, Send, X, ExternalLink } from 'lucide-react';
import { createTraceId, logger } from '@/lib/services';
import { classifyErrorForBoundary, type ClassifiedError } from '@/lib/api/errors';
import { supabase } from '@/integrations/supabase/client';
import { getSupabaseAuthKey } from '@/lib/config/environment';
import { clearAllCaches } from '@/lib/config/cacheConfig';
import { isRouterContextError, handleRouterContextError } from '@/lib/services/routerContextService';

const TELEGRAM_BOT_USERNAME = 'drugtech';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
  classified: ClassifiedError | null;
  errorCode: string;
  sessionId: string;
  timestamp: string;
}

// Generate session ID from localStorage or create new
function getSessionId(): string {
  let sessionId = sessionStorage.getItem('error_session_id');
  if (!sessionId) {
    sessionId = `SES-${Date.now().toString(36).toUpperCase()}`;
    sessionStorage.setItem('error_session_id', sessionId);
  }
  return sessionId;
}

// Get user ID from localStorage
function getUserId(): string | null {
  try {
    const authData = localStorage.getItem(getSupabaseAuthKey());
    if (authData) {
      const parsed = JSON.parse(authData);
      return parsed?.user?.id || null;
    }
  } catch {
    // ignore
  }
  return null;
}

// Build Telegram command with all error details
function buildTelegramCommand(
  errorCode: string,
  errorMessage: string,
  userId: string | null,
  sessionId: string,
  timestamp: string,
  comment?: string
): string {
  const safeMessage = errorMessage.substring(0, 100).replace(/\n/g, ' ');
  const userPart = userId ? userId.substring(0, 8) : 'anon';
  const commentPart = comment ? `\n💬 ${comment}` : '';
  
  return `/error ${errorCode}
👤 ${userPart}
🔑 ${sessionId}
🕐 ${timestamp}
❌ ${safeMessage}${commentPart}`;
}

// Error Report Modal Component
function ErrorReportModal({ 
  isOpen, 
  onClose, 
  errorCode, 
  errorMessage,
  userId,
  sessionId,
  timestamp
}: { 
  isOpen: boolean; 
  onClose: () => void; 
  errorCode: string; 
  errorMessage: string;
  userId: string | null;
  sessionId: string;
  timestamp: string;
}) {
  const [comment, setComment] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isSent, setIsSent] = useState(false);
  const [sendFailed, setSendFailed] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  if (!isOpen) return null;

  const telegramCommand = buildTelegramCommand(errorCode, errorMessage, userId, sessionId, timestamp, comment);
  const telegramDeeplink = `https://t.me/${TELEGRAM_BOT_USERNAME}`;

  const handleCopyCommand = async () => {
    try {
      await navigator.clipboard.writeText(telegramCommand);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleOpenTelegram = () => {
    window.open(telegramDeeplink, '_blank');
  };

  const handleSubmit = async () => {
    setIsSending(true);
    setSendFailed(false);
    
    try {
      const { error } = await supabase.functions.invoke('error-report', {
        body: {
          type: 'user_report',
          errorCode,
          errorMessage,
          userComment: comment,
          url: window.location.href,
          userAgent: navigator.userAgent,
          timestamp,
          userId,
          sessionId,
          environment: import.meta.env.VITE_APP_ENV || import.meta.env.MODE,
          appVersion: import.meta.env.VITE_APP_VERSION || 'unknown',
        },
      });

      if (error) throw error;
      
      setIsSent(true);
      setTimeout(() => {
        onClose();
        setIsSent(false);
        setComment('');
        setSendFailed(false);
      }, 2000);
    } catch (err) {
      console.error('Failed to send error report:', err);
      setSendFailed(true);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="w-full max-w-sm bg-background rounded-2xl shadow-xl overflow-hidden max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border sticky top-0 bg-background">
          <h3 className="text-lg font-semibold text-foreground">Сообщить об ошибке</h3>
          <button 
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-muted transition-colors"
          >
            <X className="w-5 h-5 text-muted-foreground" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {isSent ? (
            <div className="py-8 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/10 flex items-center justify-center">
                <Check className="w-8 h-8 text-green-500" />
              </div>
              <p className="text-foreground font-medium">Спасибо за обращение!</p>
              <p className="text-muted-foreground text-sm mt-1">Мы уже работаем над исправлением</p>
            </div>
          ) : (
            <>
              <div className="p-3 bg-muted rounded-lg">
                <p className="text-xs text-muted-foreground mb-1">Код ошибки:</p>
                <code className="text-sm font-mono text-foreground break-all">{errorCode}</code>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Опишите, что произошло (необязательно)
                </label>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Что вы делали перед ошибкой..."
                  className="w-full h-24 px-3 py-2 text-sm bg-background border border-border rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-primary/50"
                  maxLength={500}
                />
                <p className="text-xs text-muted-foreground mt-1 text-right">{comment.length}/500</p>
              </div>

              <button
                onClick={handleSubmit}
                disabled={isSending}
                className="w-full h-12 bg-primary text-primary-foreground rounded-xl font-medium flex items-center justify-center gap-2 hover:bg-primary/90 transition-colors disabled:opacity-50"
              >
                {isSending ? (
                  <>Отправка...</>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Отправить
                  </>
                )}
              </button>

              {/* Fallback: Manual Telegram option */}
              {sendFailed && (
                <div className="pt-4 border-t border-border space-y-3">
                  <p className="text-sm text-destructive text-center">
                    Не удалось отправить автоматически
                  </p>
                  <p className="text-xs text-muted-foreground text-center">
                    Скопируйте команду и отправьте её нашему боту в Telegram:
                  </p>
                  
                  <div className="p-3 bg-muted rounded-lg">
                    <pre className="text-xs font-mono text-foreground whitespace-pre-wrap break-all">
                      {telegramCommand}
                    </pre>
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={handleCopyCommand}
                      className="flex-1 h-10 bg-muted text-foreground rounded-xl font-medium flex items-center justify-center gap-2 hover:bg-muted/80 transition-colors text-sm"
                    >
                      {isCopied ? (
                        <>
                          <Check className="w-4 h-4 text-green-500" />
                          Скопировано
                        </>
                      ) : (
                        <>
                          <Copy className="w-4 h-4" />
                          Скопировать
                        </>
                      )}
                    </button>
                    <button
                      onClick={handleOpenTelegram}
                      className="flex-1 h-10 bg-[#0088cc] text-white rounded-xl font-medium flex items-center justify-center gap-2 hover:bg-[#0088cc]/90 transition-colors text-sm"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Открыть TG
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null, 
      classified: null,
      errorCode: '',
      sessionId: getSessionId(),
      timestamp: new Date().toISOString()
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    // Handle Router context errors via centralized service
    if (isRouterContextError(error)) {
      const retryKey = 'routerContextRetry';
      const retried = sessionStorage.getItem(retryKey);
      
      if (!retried) {
        // First occurrence - log and schedule reload
        handleRouterContextError('ErrorBoundary', error, 'reload');
        sessionStorage.setItem(retryKey, Date.now().toString());
        
        // Schedule reload after a short delay
        setTimeout(() => {
          window.location.reload();
        }, 100);
        
        // Don't show error UI - we're about to reload
        return { hasError: false, error: null, classified: null };
      } else {
        // Already retried - clear flag, log as persistent, and show error UI
        handleRouterContextError('ErrorBoundary', error, 'none');
        sessionStorage.removeItem(retryKey);
      }
    }
    
    // Special handling for other context errors - likely React/HMR loading race condition
    const isContextError = 
      (error.message?.includes('useContext') && error.message?.includes('null')) ||
      error.message?.includes('must be used within');
    if (isContextError) {
      const retryKey = 'useContextRetry';
      const retried = sessionStorage.getItem(retryKey);
      
      if (!retried) {
        // First occurrence - schedule reload and don't crash
        console.warn('[ErrorBoundary] useContext null error detected - scheduling auto-reload');
        sessionStorage.setItem(retryKey, Date.now().toString());
        
        // Schedule reload after a short delay
        setTimeout(() => {
          window.location.reload();
        }, 100);
        
        // Don't show error UI - we're about to reload
        return { hasError: false, error: null, classified: null };
      } else {
        // Already retried - clear flag and show error UI
        console.error('[ErrorBoundary] useContext null error persists after retry');
        sessionStorage.removeItem(retryKey);
      }
    }
    
    const classified = classifyErrorForBoundary(error);
    
    // Only crash for fatal UI errors
    if (!classified.shouldCrashBoundary) {
      // Log but don't crash - re-throw to let component handle
      console.warn('[ErrorBoundary] Non-fatal error caught, not crashing boundary:', error.message);
      return { hasError: false, error: null, classified: null };
    }
    
    // Generate unique error code
    const errorCode = `ERR-${Date.now().toString(36).toUpperCase()}-${Math.random().toString(36).substring(2, 6).toUpperCase()}`;
    const timestamp = new Date().toISOString();
    const sessionId = getSessionId();
    
    return { hasError: true, error, classified, errorCode, sessionId, timestamp };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const classified = classifyErrorForBoundary(error);
    
    // Only process fatal errors
    if (!classified.shouldCrashBoundary) {
      return;
    }
    
    this.setState({ errorInfo, classified });
    
    // Clear app caches on fatal error (preserves auth)
    clearAllCaches();
    
    // Log as critical for fatal UI errors
    logger.critical('React Error Boundary caught fatal error', {
      context: { 
        feature: 'error_boundary', 
        function_name: 'componentDidCatch', 
        screen_id: 'SYS-ERR', 
        trace_id: createTraceId() 
      },
      data: { 
        errorMessage: error.message,
        errorStack: error.stack?.substring(0, 500),
        componentStack: errorInfo.componentStack?.substring(0, 500),
        category: classified.category,
        severity: classified.severity,
        errorCode: this.state.errorCode,
      },
      allowList: ['errorMessage', 'errorStack', 'componentStack', 'category', 'severity', 'errorCode'],
      error_code: 'fatal_ui_error',
      error_type: 'ui',
      error,
      impact: 'single_user',
      suggested_action: 'reload',
    });
  }

  handleGoBack = () => {
    // Try to go back in history, if no history go to catalog
    if (window.history.length > 1) {
      window.history.back();
    } else {
      window.location.href = '/catalog';
    }
  };

  handleRefresh = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/catalog';
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <ErrorFallback 
          error={this.state.error}
          errorCode={this.state.errorCode}
          sessionId={this.state.sessionId}
          timestamp={this.state.timestamp}
          onGoBack={this.handleGoBack}
          onRefresh={this.handleRefresh}
          onGoHome={this.handleGoHome}
        />
      );
    }

    return this.props.children;
  }
}

// Separate functional component for the fallback UI (to use hooks)
function ErrorFallback({ 
  error, 
  errorCode,
  sessionId,
  timestamp,
  onGoBack, 
  onRefresh, 
  onGoHome 
}: { 
  error: Error | null; 
  errorCode: string;
  sessionId: string;
  timestamp: string;
  onGoBack: () => void; 
  onRefresh: () => void; 
  onGoHome: () => void;
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  
  const userId = getUserId();
  const telegramCommand = buildTelegramCommand(errorCode, error?.message || 'Unknown error', userId, sessionId, timestamp);

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(telegramCommand);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <div className="min-h-screen min-h-[100dvh] flex flex-col bg-background">
      <div
        className="w-full flex flex-col min-h-screen min-h-[100dvh]"
        style={{ maxWidth: '420px', margin: '0 auto' }}
      >
        {/* Header */}
        <div className="flex items-center px-4 py-4 bg-background border-b border-border">
          <button
            onClick={onGoBack}
            className="w-10 h-10 flex items-center justify-center -ml-2 rounded-full hover:bg-muted transition-colors"
            aria-label="Назад"
          >
            <ChevronLeft className="w-6 h-6 text-foreground" />
          </button>
          <h1 className="flex-1 text-center text-lg font-medium text-foreground pr-10">
            Ошибка
          </h1>
        </div>

        {/* Content */}
        <div className="flex-1 px-6 py-8 flex flex-col items-center text-center overflow-auto">
          <div className="w-20 h-20 rounded-full bg-destructive/10 flex items-center justify-center mb-6">
            <AlertTriangle className="w-10 h-10 text-destructive" />
          </div>

          <h2 className="text-2xl font-bold text-foreground mb-2">
            Что-то пошло не так
          </h2>

          <p className="text-muted-foreground mb-6 max-w-sm">
            Произошла непредвиденная ошибка. Ваши данные в безопасности.
          </p>

          {/* Error Code Block */}
          <div className="w-full max-w-xs mb-6">
            <div className="p-4 bg-muted rounded-xl">
              <p className="text-xs text-muted-foreground mb-2">Код ошибки для поддержки:</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-sm font-mono text-foreground break-all">
                  {errorCode}
                </code>
                <button
                  onClick={handleCopyCode}
                  className="shrink-0 w-8 h-8 flex items-center justify-center rounded-lg bg-background hover:bg-background/80 transition-colors"
                  aria-label="Скопировать"
                >
                  {isCopied ? (
                    <Check className="w-4 h-4 text-green-500" />
                  ) : (
                    <Copy className="w-4 h-4 text-muted-foreground" />
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Action buttons */}
          <div className="w-full space-y-3 max-w-xs">
            <button
              onClick={onRefresh}
              className="w-full h-12 bg-primary text-primary-foreground rounded-xl font-medium flex items-center justify-center gap-2 hover:bg-primary/90 transition-colors"
            >
              <RefreshCw className="w-5 h-5" />
              Обновить страницу
            </button>

            <button
              onClick={onGoHome}
              className="w-full h-12 bg-muted text-foreground rounded-xl font-medium hover:bg-muted/80 transition-colors"
            >
              На главную
            </button>

            <button
              onClick={() => setIsModalOpen(true)}
              className="w-full h-12 border border-border text-foreground rounded-xl font-medium flex items-center justify-center gap-2 hover:bg-muted transition-colors"
            >
              <MessageSquare className="w-5 h-5" />
              Сообщить об ошибке
            </button>
          </div>

          {/* Error details (development only) */}
          {import.meta.env.DEV && error && (
            <div className="mt-6 w-full max-w-xs p-4 bg-muted rounded-lg text-left">
              <p className="text-xs font-mono text-muted-foreground break-all">
                {error.message}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Error Report Modal */}
      <ErrorReportModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        errorCode={errorCode}
        errorMessage={error?.message || 'Unknown error'}
        userId={userId}
        sessionId={sessionId}
        timestamp={timestamp}
      />
    </div>
  );
}

export default ErrorBoundary;
